import { OutlineElement } from '../../base/outline-element/outline-element';
import { html, TemplateResult, CSSResultGroup } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './tufts-layout.css.lit';
import componentStylesEqual from './tufts-layout-equal.css.lit';
import { classMap } from 'lit/directives/class-map.js';
import { ResizeController } from '../../controllers/resize-controller';
import { SlotNGController } from '../../controllers/slot-ng-controller';

enum verticalAlignmentOptions {
  'start',
  'center',
  'end',
}

/**
 * The tufts layout component
 * @element tufts-layout
 *
 * @slot primary-content - First slot of element.
 * @slot secondary-content - Second slot of element.
 * @slot tertiary-content - Third slot of element.
 *
 * @attr layout-template - layout template
 * @attr columns - number of columns
 * @attr min-width - Minimum width to display more than 1 column
 * @attr equal-height - force slots to have the same height
 */
@customElement('tufts-layout')
export class TuftsLayout extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, componentStylesEqual];

  ngSlot = new SlotNGController(this);
  resizeController: ResizeController;

  /**
   * The resizeController is initialized here to allow observation of tufts-container
   * VerticalTriggerWidth is not set yet when constructor runs
   */
  connectedCallback() {
    super.connectedCallback();

    // If tufts-container cannot be found - use tufts-layout (this) by default
    const triggerElement = this.closest('tufts-container') || this;

    this.resizeController = new ResizeController(this, {
      breakpoints: [this.verticalTriggerWidth],
      triggerElement,
    });
  }

  /**
   * Whether or not all rows will have equal height.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'equal-height',
  })
  equalHeight = false;

  /**
   * Layout template
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'layout-template',
  })
  template = '6c-6c';

  /**
   * Vertical Alignment (start, center, end)
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'vertical-align',
    converter: verticalAlign => {
      if (verticalAlign && !(verticalAlign in verticalAlignmentOptions)) {
        verticalAlign = 'start';
      }
      return verticalAlign;
    },
  })
  verticalAlign: verticalAlignmentOptions;

  /**
   * Width at which layout changes from horizontal to vertical
   */
  @property({
    type: String,
    reflect: true,
    converter: Number,
    attribute: 'vertical-trigger-width',
  })
  verticalTriggerWidth = 1080;

  /**
   * Space gap for mobile layout
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'mobile-gap',
  })
  mobileGap: string;

  isMobile: boolean;

  /**
   * Debug mode
   */
  @property({
    type: Boolean,
    reflect: true,
  })
  debug = false;

  conditionalLightDomSlot(slotName: string) {
    if (this.ngSlot.exist(slotName)) {
      return html`<slot name="${slotName}"></slot>`;
    }
    return null;
  }

  getColumnsArray() {
    const template = this.template.split('-');
    const columnsArray = [];
    let columnsIndex = 1;

    for (let i = 0; i < template.length; i++) {
      const [columnUnits, columnType] = template[i].split(/(?=[a-z])/);

      if (columnType === 'c') {
        columnsArray.push(`${columnsIndex}/span ${columnUnits}`);
      }
      columnsIndex += +columnUnits;
    }
    return columnsArray;
  }

  debugTemplate(): TemplateResult | null {
    const twelveColumnsTemplate = Array(12)
      .fill(`<div class="debug-column"></div>`)
      .join('');
    return html`
      <div class="debug-grid ${this.isMobile ? 'mobile' : null}">
        ${unsafeHTML(twelveColumnsTemplate)}
      </div>
    `;
  }

  /**
   * Handles the mobile sidenav behavior, ensuring consistent display and functionality across devices.
   */
  handleMobileSidenav() {
    // Get all main sidenav elements from the document
    const mainSidenavElements = document.querySelectorAll('tufts-sidebar');

    // If main sidenav elements exist:
    if (mainSidenavElements.length) {
      // Iterate through each main sidenav element
      mainSidenavElements.forEach(sidenavElement => {
        // Set or remove the 'mobile' attribute based on current device type
        if (this.isMobile) {
          sidenavElement.setAttribute('mobile', `${this.isMobile}`);
        } else {
          sidenavElement.removeAttribute('mobile');
        }
      });

      // If the current device is mobile:
      if (this.isMobile) {
        // Check if a mobile-specific sidenav already exists
        if (mainSidenavElements.length > 1) {
          // If it does, no further action is needed
          return;
        }

        // Create a clone of the first main sidenav element for mobile use
        const clonedSidenavForMobile =
          mainSidenavElements[0].cloneNode() as Element;

        // Get the breadcrumbs container for proper placement
        const breadcrumbsContainer = document
          .querySelector('#block-outline-frontend-breadcrumbs')
          ?.querySelector('tufts-container');

        // If both the cloned sidenav and breadcrumbs container exist:
        if (clonedSidenavForMobile && breadcrumbsContainer) {
          // Add attributes to the cloned sidenav for mobile styling and placement
          clonedSidenavForMobile.setAttribute('mobile', 'true');
          clonedSidenavForMobile.setAttribute('top', 'true');

          // Insert the cloned sidenav at the beginning of the breadcrumbs container
          breadcrumbsContainer.insertBefore(
            clonedSidenavForMobile,
            breadcrumbsContainer.firstChild
          );
        }
      }
    }
  }

  updated() {
    this.handleMobileSidenav();
  }

  render(): TemplateResult {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;

    const classes = {
      'grid-wrapper': !this.equalHeight,
      'grid-wrapper-tufts-layout': this.equalHeight,
      'mobile': this.isMobile,
    };

    this.style.setProperty(
      '--layout-vertical-alignment',
      this.verticalAlign as unknown as string
    );

    this.getColumnsArray().forEach((style, index) => {
      this.style.setProperty(`--content-area-${index + 1}`, style);
    });

    return html`
      ${this.mobileGap
        ? html`
            <style>
              .grid-wrapper.mobile {
                display: flex;
                flex-direction: column;
                gap: ${this.mobileGap};
              }
            </style>
          `
        : ''}
      <div class=${classMap(classes)}>
        ${this.conditionalLightDomSlot('content-area-1')}
        ${this.conditionalLightDomSlot('content-area-2')}
        ${this.conditionalLightDomSlot('content-area-3')}
      </div>
      ${this.debug ? this.debugTemplate() : null}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-layout': TuftsLayout;
  }
}
